import React, { useState, useEffect, useRef, useContext } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import { util } from "../../config/util";
import { api } from "../../config/api";
import SubscriptionCardModal, {
  BillingCardInfo,
} from "./components/SubscriptionCardModal";
import { UserInfoModel } from "../../models/user/UserInfo";
import { Imp } from "../../models/payment/iamport/IMP";
import {
  IamportPaymentRequestModel,
  IamportPayMethod,
  ImpCallbackResponseModel,
} from "../../models/payment/iamport/ImpModels";
import AlertContext from "../../contexts/AlertContext";
import DupLoginContext from "../../contexts/DupLoginContext";
import { Cookies } from "../../constants/Cookies";
import { pages } from "../../constants/PagePaths";
import GlobalEnvironments from "../../modules/globalEnvironment/GlobalEnvironment";
import { checkAndProcessExpiredPromotions } from "../../services/promotionService";
import { logEvent, getPreviousPath } from "../../services/eventLog";
import SubscriptionOverlapModal from "../../components/modal/subscription/SubscriptionOverlapModal";
import { termsOfService, privacyPolicy, refundPolicy } from "../../constants/Terms";

// 애니메이션 CSS 추가
const animationStyles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

interface SubscriptionProduct {
  id: number;
  name: string;
  isPopular: boolean;
  // 새로운 4가지 결제 유형
  regular_monthly_price: number; // 1개월 일반결제 (기준가, 가장 비쌈)
  regular_yearly_price: number; // 1년 일반결제 (약간 할인)
  subscription_monthly_price: number; // 월간 구독 결제 (매월 자동결제)
  subscription_yearly_price: number; // 연간 구독 결제 (1년치 자동결제)
  // 마케팅용 월 환산 금액들
  marketing_monthly_regular: number; // 1년 일반결제시 월 환산 (마케팅용)
  marketing_monthly_subscription: number; // 연간 구독시 월 환산 (마케팅용)
  // 기존 필드들 (하위 호환성 유지)
  origin_price?: number; // 기존 월간 가격
  yearly_price?: number; // 기존 연간 가격
  features: string[];
  description: string;
  category?: string; // API 응답 필드
}

interface PromotionEligibility {
  isEligible: boolean;
  promotionType: string;
  description: string;
  benefitDays: number;
}

interface FeatureRow {
  category?: string;
  feature: string;
  products: boolean[]; // 각 상품별 지원 여부
}

const SubscriptionPaymentContainer: React.FC = () => {
  const history = useHistory();
  const alertContext = useContext(AlertContext);
  const dupLogin = useContext(DupLoginContext);
  const [isYearly, setIsYearly] = useState(true); // 연간 결제가 기본
  const [isSubscription, setIsSubscription] = useState(true); // 구독 결제 (기본값 true)
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);

  // 선택된 각 상품의 권한 캐시 (productId → Set<권한명>)
  const [selectedProductPermissions, setSelectedProductPermissions] = useState<Map<number, Set<string>>>(new Map());

  // 구독 체크박스 변경 시 선택 상품 초기화
  useEffect(() => {
    setSelectedProducts([]);
    setSelectedProductPermissions(new Map());
  }, [isSubscription]);
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const [tablePosition, setTablePosition] = useState({ left: 0, width: 0 });
  const [scrollLeft, setScrollLeft] = useState(0);
  const [cellWidths, setCellWidths] = useState<number[]>([]);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfoModel | undefined>();
  const [products, setProducts] = useState<SubscriptionProduct[]>([]);
  const [isProductsLoading, setIsProductsLoading] = useState(true);
  const [savedCardInfo, setSavedCardInfo] = useState<BillingCardInfo | null>(
    null
  );
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    isActive: boolean;
    subscriptionId?: string;
    nextPaymentDate?: string;
  }>({
    isActive: false,
    subscriptionId: undefined,
    nextPaymentDate: undefined
  });
  const [showCardModal, setShowCardModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [promotionEligibility, setPromotionEligibility] = useState<PromotionEligibility | null>(null);

  // Phase 2: 중복 구독 체크 모달 state
  const [overlapModalOpen, setOverlapModalOpen] = useState(false);
  const [overlapData, setOverlapData] = useState<any>(null);
  const [pendingProductId, setPendingProductId] = useState<number | null>(null);

  // 기간제 이용권 결제 수단 선택 (기본값: 카드)
  const [payMethod, setPayMethod] = useState<IamportPayMethod>("card");

  // 약관 동의 상태
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeRefund, setAgreeRefund] = useState(false);
  const [agreeAll, setAgreeAll] = useState(false);

  // 약관 모달 상태
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);

  // 전체 동의 핸들러
  const handleAgreeAll = (checked: boolean) => {
    setAgreeAll(checked);
    setAgreeTerms(checked);
    setAgreePrivacy(checked);
    setAgreeRefund(checked);
  };

  // promotionEligibility state 변경 추적을 위한 useEffect
  useEffect(() => {
    console.log('[SubscriptionPayment] 🔄 promotionEligibility state 변경:', {
      이전값: 'N/A',
      새로운값: promotionEligibility,
      타입: typeof promotionEligibility,
      isEligible: promotionEligibility?.isEligible,
      타임스탬프: new Date().toISOString()
    });
  }, [promotionEligibility]);

  const tableRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLTableSectionElement>(null);

  // 프로모션 자격 확인 함수
  const checkPromotionEligibility = async () => {
    try {
      console.log('[SubscriptionPayment] 🎯 프로모션 자격 확인 시작');
      console.log('[SubscriptionPayment] 현재 시간:', new Date().toISOString());
      
      // 현재 활성 프로모션 상태 확인
      const promotionStatus = await checkAndProcessExpiredPromotions();
      console.log('[SubscriptionPayment] 📊 프로모션 상태:', promotionStatus);
      
      // 원래 프로모션 자격 API로 복원
      console.log('[SubscriptionPayment] 🔍 프로모션 자격 API 호출 시작...');
      console.log('[SubscriptionPayment] 🔍 getPromotionEligibilityAsync API 호출');
      
      const eligibilityResponse = await dupLogin.checkDupLogin(() =>
        api.getPromotionEligibilityAsync()
      );
      
      console.log('[SubscriptionPayment] 📝 프로모션 자격 API 전체 응답:', eligibilityResponse);
      console.log('[SubscriptionPayment] 📝 응답 상세 분석:');
      console.log('  - HTTP Status:', eligibilityResponse?.status);
      console.log('  - Status Text:', eligibilityResponse?.statusText);
      console.log('  - Headers:', eligibilityResponse?.headers);
      console.log('  - Data:', eligibilityResponse?.data);
      console.log('  - Data JSON:', JSON.stringify(eligibilityResponse?.data, null, 2));
      
      if (eligibilityResponse?.status === 200) {
        console.log('[SubscriptionPayment] ✅ API 응답 성공 (status: 200)');
        console.log('[SubscriptionPayment] 📋 API 응답 코드:', eligibilityResponse.data?.code);
        
        if (eligibilityResponse.data?.code === 1) {
          console.log('[SubscriptionPayment] ✅ API 응답 코드 성공 (code: 1)');
          
          const eligibilityData = eligibilityResponse.data.data || eligibilityResponse.data;
          console.log('[SubscriptionPayment] 🔍 추출된 자격 데이터:', eligibilityData);
          console.log('[SubscriptionPayment] 🔍 자격 데이터 상세 분석:');
          console.log('  - user_id:', eligibilityData?.user_id);
          console.log('  - eligibility:', eligibilityData?.eligibility);
          console.log('  - current_promotion:', eligibilityData?.current_promotion);
          console.log('  - recommendations:', eligibilityData?.recommendations);
          
          // eligibility 객체 내부 구조 분석
          if (eligibilityData?.eligibility) {
            console.log('[SubscriptionPayment] 🔍 eligibility 객체 상세 분석:', eligibilityData.eligibility);
            console.log('[SubscriptionPayment] 🔍 eligibility 객체의 모든 키:', Object.keys(eligibilityData.eligibility));
            console.log('[SubscriptionPayment] 🔍 eligibility 타입:', typeof eligibilityData.eligibility);
            console.log('[SubscriptionPayment] 🔍 eligibility JSON:', JSON.stringify(eligibilityData.eligibility, null, 2));
            
            // eligibility 객체 내부의 각 속성 확인
            Object.keys(eligibilityData.eligibility).forEach(key => {
              const value = eligibilityData.eligibility[key];
              console.log(`[SubscriptionPayment] 🔑 eligibility.${key}:`, value, `(타입: ${typeof value})`);
              
              // 만약 값이 객체라면 더 깊이 분석
              if (typeof value === 'object' && value !== null) {
                console.log(`[SubscriptionPayment] 📎 eligibility.${key} 상세:`, JSON.stringify(value, null, 2));
                if (!Array.isArray(value)) {
                  console.log(`[SubscriptionPayment] 📎 eligibility.${key} 키들:`, Object.keys(value));
                }
              }
            });
          }
          
          // 프로모션 자격 확인 로직 (eligibility 객체 기반)
          let hasFirstSubscriptionEligibility = false;
          
          console.log('[SubscriptionPayment] 🔍 프로모션 자격 확인 시작');
          
          // eligibility 객체에서 first_subscription_2weeks 자격 직접 확인
          if (eligibilityData?.eligibility) {
            console.log('[SubscriptionPayment] 📋 eligibility 객체에서 자격 확인');
            
            // 가능한 키 패턴들 확인 (API 응답 기반)
            const possibleKeys = [
              'eligible_for_first_subscription',  // ← API에서 실제 사용하는 키
              'first_subscription_2weeks',
              'firstSubscription2weeks', 
              'first_subscription',
              'firstSubscription'
            ];
            
            for (const key of possibleKeys) {
              const value = eligibilityData.eligibility[key];
              if (value !== undefined) {
                console.log(`[SubscriptionPayment] 🔑 ${key} 키 발견:`, value);
                if (value === true || value === 'eligible' || value === 1) {
                  console.log(`[SubscriptionPayment] ✅ ${key}에서 자격 확인!`);
                  hasFirstSubscriptionEligibility = true;
                  break;
                }
              }
            }
            
            // 만약 직접 키가 없다면 중첩 객체 확인
            if (!hasFirstSubscriptionEligibility) {
              Object.keys(eligibilityData.eligibility).forEach(key => {
                const value = eligibilityData.eligibility[key];
                if (typeof value === 'object' && value !== null) {
                  console.log(`[SubscriptionPayment] 🔍 중첩 객체 ${key} 확인:`, value);
                  if (value.first_subscription_2weeks === true || 
                      value.eligible === true ||
                      value.is_eligible === true) {
                    console.log(`[SubscriptionPayment] ✅ ${key} 중첩 객체에서 자격 확인!`);
                    hasFirstSubscriptionEligibility = true;
                  }
                }
              });
            }
          }
          
          // 다른 위치에서도 확인 (fallback)
          if (!hasFirstSubscriptionEligibility && eligibilityData?.eligible_promotions) {
            console.log('[SubscriptionPayment] 📋 eligible_promotions 배열에서 확인');
            const firstSubscriptionPromo = eligibilityData.eligible_promotions.find(
              (promo: any) => promo.promotion_type === 'first_subscription_2weeks'
            );
            if (firstSubscriptionPromo) {
              console.log('[SubscriptionPayment] ✅ eligible_promotions에서 자격 확인!');
              hasFirstSubscriptionEligibility = true;
            }
          }
          
          console.log('[SubscriptionPayment] 🎯 최종 자격 판정:', hasFirstSubscriptionEligibility);
          
          // 자격 판정 결과에 따른 상태 설정
          if (hasFirstSubscriptionEligibility) {
            console.log('[SubscriptionPayment] 🎉 첫 구독 프로모션 자격 있음!');
            
            const newPromotionEligibility = {
              isEligible: true,
              promotionType: 'first_subscription_2weeks',
              description: '첫 구독 시 2주 무료 혜택',
              benefitDays: 14
            };
            
            console.log('[SubscriptionPayment] 💾 설정할 promotionEligibility:', newPromotionEligibility);
            setPromotionEligibility(newPromotionEligibility);
            
          } else {
            console.log('[SubscriptionPayment] ❌ 첫 구독 프로모션 자격 없음');
            setPromotionEligibility(null);
          }
          
        } else {
          console.log('[SubscriptionPayment] ❌ API 응답 코드 실패:', {
            code: eligibilityResponse.data?.code,
            message: eligibilityResponse.data?.message
          });
          setPromotionEligibility(null);
        }
      } else {
        console.log('[SubscriptionPayment] ❌ API 응답 상태 실패:', {
          status: eligibilityResponse?.status,
          statusText: eligibilityResponse?.statusText
        });
        setPromotionEligibility(null);
      }
    } catch (error) {
      console.error('[SubscriptionPayment] ❌ 프로모션 자격 확인 오류:', error);
      console.error('[SubscriptionPayment] 오류 상세:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      setPromotionEligibility(null);
    }
  };

  // 🛟 모바일 인앱 브라우저에서 PortOne m_redirect_url로 돌아온 경우 처리
  // 원인: NAVER 인앱 등에서 Imp.requestPay의 JS 콜백(handleRegularPaymentCallback)이
  //      호출되지 않고 m_redirect_url로 강제 리다이렉트된다. 이때 PaymentComplete API가
  //      호출되지 않아 이용권이 발급되지 않는 사고가 발생했다 (2026-04-16 imp_382287392483).
  // 이 useEffect는 /subscription 진입 시 쿼리스트링에 imp_uid가 있으면
  // 해당 결제를 PaymentComplete로 전달해 이용권 발급을 완료한다.
  // (백엔드 PaymentApproval 웹훅에도 자동 복구 로직이 있지만, 프론트에서도 직접 처리해
  //  사용자에게 즉시 완료 피드백을 준다.)
  useEffect(() => {
    const search = window.location.search;
    if (!search) return;
    const parsed = new URLSearchParams(search);
    const impUid = parsed.get("imp_uid");
    const impSuccess = parsed.get("imp_success");
    if (!impUid && impSuccess === null) return;

    if (impSuccess === "false") {
      alertContext.showAlert("결제가 취소되었습니다.");
      history.replace(pages.subscription);
      return;
    }
    if (!impUid) return;

    (async () => {
      try {
        const productIdsStr = parsed.get("product_ids") || "";
        const productIds: number[] = productIdsStr
          ? productIdsStr
              .split(",")
              .map((id) => parseInt(id.trim(), 10))
              .filter((n) => !isNaN(n))
          : [];
        const actualAmount: number = parseInt(
          parsed.get("actual_amount") || "0",
          10
        );
        const isYearlyFromQuery: boolean = parsed.get("is_yearly") === "true";
        const payMethodFromQuery =
          (parsed.get("pay_method") as IamportPayMethod) ||
          IamportPayMethod.card;

        const paymentData: any = {
          imp_uid: impUid,
          merchant_uid: parsed.get("merchant_uid") || "",
          pay_method: payMethodFromQuery,
          paid_amount: actualAmount,
          success: true,
          custom_data: {
            product_ids: productIds,
            actual_amount: actualAmount,
            is_subscription: false,
            is_yearly: isYearlyFromQuery,
          },
        };

        const completeRes = await dupLogin.checkDupLogin(() =>
          api.postPaymentCompleteAsync(paymentData)
        );

        if (completeRes?.status === 200) {
          alertContext.showAlert("결제가 완료되었습니다.", () => {
            history.replace(pages.paymentComplete);
          });
          return;
        }

        alertContext.showAlert(
          "결제가 접수되었습니다. 이용권 반영까지 잠시 시간이 걸릴 수 있습니다.\n마이페이지 결제이력에서 확인해주세요.",
          () => {
            history.replace(pages.mypage + "/payment");
          }
        );
      } catch (err: any) {
        // axios는 4xx/5xx 응답을 throw하므로 여기서 분기한다.
        // "이미 처리된 주문입니다." 500 응답은 백엔드 자동 복구가 먼저 완료된 경우이므로 성공 취급.
        const resp = err?.response;
        const bodyRaw = resp?.data;
        const body =
          typeof bodyRaw === "string" ? bodyRaw : JSON.stringify(bodyRaw || "");
        if (resp?.status === 500 && body.indexOf("이미 처리") !== -1) {
          alertContext.showAlert("결제가 완료되었습니다.", () => {
            history.replace(pages.paymentComplete);
          });
          return;
        }

        console.error("모바일 결제 복귀 처리 오류:", err);
        alertContext.showAlert(
          "결제 결과를 확인하지 못했습니다.\n마이페이지 결제이력에서 확인해주세요.",
          () => {
            history.replace(pages.mypage + "/payment");
          }
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 컴포넌트 마운트 시 사용자 정보, 구독 상태, 상품 정보 로드
  useEffect(() => {
    const loadInitialData = async () => {
      const key = util.getCookie(Cookies.authKey);

      // 계측: 결제 화면 진입 (source=직전 경로, 서버가 체험 남은 일수를 meta 에 덧붙임)
      logEvent("payment_page_view", getPreviousPath() || "direct", {
        from: getPreviousPath() || null,
        path: window.location.pathname,
        search: window.location.search || null,
        referrer: document.referrer || null,
        express_checkout: !!(
          localStorage.getItem("express_selected_plans") ||
          localStorage.getItem("selected_plans_from_landing")
        ),
      });

      // Express Checkout에서 전달된 정보 확인 및 처리
      const checkExpressCheckoutData = () => {
        // 1. 선택된 플랜 정보 복원
        const expressPlansData = localStorage.getItem('express_selected_plans') || localStorage.getItem('selected_plans_from_landing');
        if (expressPlansData) {
          try {
            const planInfo = JSON.parse(expressPlansData);
            console.log('🔄 Express Checkout 플랜 정보 복원:', planInfo);
            
            // 선택된 상품 ID 설정
            if (planInfo.plans && Array.isArray(planInfo.plans)) {
              const planIds = planInfo.plans.map((plan: any) => parseInt(plan.id));
              setSelectedProducts(planIds);
              console.log('📋 선택된 상품 복원:', planIds);
            }
            
            // 결제 옵션 설정
            if (planInfo.isMonthly !== undefined) {
              setIsYearly(!planInfo.isMonthly);
              console.log('📅 결제 주기 설정:', planInfo.isMonthly ? '월간' : '연간');
            }
            
            if (planInfo.isSubscription !== undefined) {
              setIsSubscription(planInfo.isSubscription);
              console.log('💳 결제 방식 설정:', planInfo.isSubscription ? '구독' : '일반');
            }
            
            // 처리 완료 후 localStorage에서 제거
            localStorage.removeItem('express_selected_plans');
            localStorage.removeItem('selected_plans_from_landing');
            
          } catch (error) {
            console.error('❌ Express Checkout 플랜 정보 파싱 실패:', error);
          }
        }

        // 2. 카드 정보 복원
        const expressCardData = localStorage.getItem('express_card_info');
        if (expressCardData) {
          try {
            const cardInfo = JSON.parse(expressCardData);
            console.log('💳 Express Checkout 카드 정보 복원:', cardInfo);
            setSavedCardInfo(cardInfo);
            
            // 처리 완료 후 localStorage에서 제거
            localStorage.removeItem('express_card_info');
            
          } catch (error) {
            console.error('❌ Express Checkout 카드 정보 파싱 실패:', error);
          }
        }

        // 3. 사용자 정보 복원 (필요한 경우)
        const expressUserData = localStorage.getItem('express_user_info');
        if (expressUserData) {
          try {
            const userInfo = JSON.parse(expressUserData);
            console.log('👤 Express Checkout 사용자 정보 복원:', userInfo);
            
            // 처리 완료 후 localStorage에서 제거
            localStorage.removeItem('express_user_info');
            
          } catch (error) {
            console.error('❌ Express Checkout 사용자 정보 파싱 실패:', error);
          }
        }
      };

      // Express Checkout 데이터 복원 실행
      checkExpressCheckoutData();

      // 상품 정보 로드 (인증 필요 없음)
      try {
        setIsProductsLoading(true);
        
        // 캐시 강제 클리어 (디버깅용)
        // localStorage.removeItem("subscription_prices");
        
        console.log("상품 API 호출 시작...");
        const productsRes = await api.getSubscriptionProductsAsync();
        console.log("상품 API 응답:", productsRes);
        
        // 상품 데이터 상세 로그
        if (productsRes?.data) {
          console.log("🔍 전체 API 응답 구조:", productsRes.data);
          console.log("🔍 API data 타입:", typeof productsRes.data.data, Array.isArray(productsRes.data.data));
          
          if (productsRes.data.data && Array.isArray(productsRes.data.data)) {
            console.log("🔍 전체 상품 데이터:", productsRes.data.data);
            
            // 설계내역 상품 찾기
            const designProduct = productsRes.data.data.find((p: any) => p.name === '설계내역');
            if (designProduct) {
              console.log("📊 설계내역 상품 API 응답 데이터:", {
                name: designProduct.name,
                regular_monthly_price: designProduct.regular_monthly_price,
                regular_yearly_price: designProduct.regular_yearly_price,
                subscription_monthly_price: designProduct.subscription_monthly_price,
                subscription_yearly_price: designProduct.subscription_yearly_price,
                marketing_monthly_regular: designProduct.marketing_monthly_regular,
                marketing_monthly_subscription: designProduct.marketing_monthly_subscription,
                "전체 필드": designProduct
              });
            }
          } else {
            console.log("❌ productsRes.data.data가 배열이 아닙니다:", productsRes.data.data);
          }
        }

        if (productsRes?.status === 200) {
          // API 응답 상태 확인
          if (productsRes.data?.code !== 1) {
            console.error("API 응답 코드 에러:", productsRes.data?.code, productsRes.data?.message);
            alertContext.showAlert(productsRes.data?.message || "상품 정보를 불러오는데 실패했습니다.");
            return;
          }

          // API 응답 데이터 검증 - 새로운 응답 구조 대응
          const rawProducts = productsRes.data.data?.products || productsRes.data.products;
          console.log("추출한 상품 데이터:", rawProducts);
          
          // 각 상품별 상세 가격 정보 로깅
          if (Array.isArray(rawProducts)) {
            rawProducts.forEach((product: any, index: number) => {
              console.log(`💰 [상품 ${index + 1}] ${product.name} 가격 데이터:`, {
                id: product.id,
                origin_price: product.origin_price,
                yearly_price: product.yearly_price,
                subscription_monthly_price: product.subscription_monthly_price,
                subscription_yearly_price: product.subscription_yearly_price,
                original_price: product.original_price,
                monthly_price: product.monthly_price
              });
            });
          }

          if (!Array.isArray(rawProducts)) {
            console.error("Products data is not an array:", rawProducts);
            alertContext.showAlert("상품 데이터 형식이 올바르지 않습니다.");
            return;
          }

          // API 응답 데이터를 인터페이스에 맞게 변환 (새로운 4가지 결제 유형 지원)
          const productsData = rawProducts
            .filter((product: any) => {
              if (!product || typeof product !== "object") {
                console.warn("Invalid product object:", product);
                return false;
              }
              if (!product.id) {
                console.warn("Product missing ID:", product);
                return false;
              }
              return true;
            })
            .map((product: any) => ({
              id: product.id,
              name: product.name || "Unknown Product",
              isPopular:
                product.category === "인기" || product.is_popular || false,
              // 새로운 4가지 결제 유형
              regular_monthly_price: product.regular_monthly_price || product.origin_price || 0,
              regular_yearly_price: product.regular_yearly_price || product.yearly_price || 0,
              subscription_monthly_price: product.subscription_monthly_price || product.monthly_price || 0,
              subscription_yearly_price: product.subscription_yearly_price || 0,
              // 마케팅용 월 환산 금액들
              marketing_monthly_regular: product.marketing_monthly_regular || Math.floor((product.regular_yearly_price || product.yearly_price || 0) / 12) || 0,
              marketing_monthly_subscription: product.marketing_monthly_subscription || Math.floor((product.subscription_yearly_price || 0) / 12) || 0,
              // 기존 필드들 (하위 호환성)
              origin_price: product.original_price || product.origin_price || 0,
              yearly_price: product.yearly_price || 0,
              features: product.features || [],
              description: product.description || "",
              category: product.category || "",
            }));

          // 인기 상품을 상단으로 정렬
          const sortedProducts = productsData.sort((a: SubscriptionProduct, b: SubscriptionProduct) => {
            if (a.isPopular && !b.isPopular) return -1;
            if (!a.isPopular && b.isPopular) return 1;
            return 0;
          });

          setProducts(sortedProducts);

          // 캐싱 처리
          localStorage.setItem(
            "subscription_prices",
            JSON.stringify({
              data: productsData,
              timestamp: Date.now(),
            })
          );
        } else {
          console.error("API 응답 에러:", {
            status: productsRes?.status,
            code: productsRes?.data?.code,
            fullResponse: productsRes
          });
          alertContext.showAlert("상품 정보를 불러오는데 실패했습니다.");
        }
      } catch (error: any) {
        console.error("상품 정보 로드 실패:", error);
        console.error("에러 상세:", {
          message: error?.message,
          response: error?.response,
          status: error?.response?.status,
          data: error?.response?.data
        });
        alertContext.showAlert(
          "상품 정보를 불러오는데 실패했습니다. 다시 시도해주세요."
        );

        // 에러 발생 시 빈 배열로 설정하여 페이지 크래시 방지
        setProducts([]);
      } finally {
        setIsProductsLoading(false);
      }

      if (key) {
        // 사용자 정보 로드
        const info = await dupLogin.checkDupLogin(() => api.getMyInfo());
        setUserInfo(info?.data);
        if (!info) {
          return;
        }

        // 저장된 카드 정보 확인
        await loadBillingInfo();

        // 프로모션 자격 확인 (프로모션 기능 비활성화)
        if (false) {
          await checkPromotionEligibility();
        }
      }
    };

    loadInitialData();
  }, []);


  // 기능 테이블 데이터 (요구사항에 맞게 7개 상품)
  const featureData: FeatureRow[] = [
    {
      feature: "엑셀파일 업로드 가능",
      products: [true, true, false, true, false, false, false],
    },
    {
      category: "설계자동화",
      feature: "",
      products: [false, false, false, false, false, false, false],
    },
    {
      feature: "설계도서",
      products: [true, false, true, false, false, true, false],
    },
    {
      feature: "표지(갑지)",
      products: [true, false, true, false, true, true, false],
    },
    {
      feature: "원가계산서",
      products: [true, false, true, false, true, true, false],
    },
    {
      feature: "총괄내역서",
      products: [true, false, true, false, true, true, false],
    },
    {
      feature: "내역서",
      products: [true, false, true, false, true, true, false],
    },
    {
      feature: "일위대가",
      products: [true, false, true, false, false, true, false],
    },
    {
      feature: "단가산출",
      products: [true, false, true, false, false, true, false],
    },
    {
      feature: "기계경비",
      products: [true, false, true, false, false, true, false],
    },
    {
      feature: "노임단가",
      products: [true, false, true, false, false, true, false],
    },
    {
      feature: "자재단가",
      products: [true, false, true, false, false, true, false],
    },
    {
      feature: "예정공정표",
      products: [true, false, true, false, false, true, false],
    },
    {
      feature: "동원인력계획표",
      products: [true, false, true, false, false, true, false],
    },
    {
      feature: "표준품셈 페이지정보 제공",
      products: [true, false, true, false, true, true, false],
    },
    {
      feature: "일위/단산 산출근거 제공",
      products: [true, false, true, false, false, true, false],
    },
    {
      feature: "일위/단산 합계만 제공",
      products: [true, false, false, false, true, true, false],
    },
    {
      feature: "자재단가 페이지 제공",
      products: [true, false, true, false, false, true, false],
    },
    {
      feature: "자재단가 합계만 제공",
      products: [true, false, false, false, true, true, false],
    },
    {
      feature: "견적용 조사단가 지원",
      products: [true, false, false, false, true, true, false],
    },
    {
      category: "엑셀 자동화",
      feature: "",
      products: [false, false, false, false, false, false, false],
    },
    {
      feature: "파일 업로드 여부",
      products: [true, true, false, false, false, false, false],
    },
    {
      feature: "계약/착공내역서",
      products: [true, true, false, false, false, false, false],
    },
    {
      feature: "계약/착공 서류",
      products: [true, true, false, false, false, false, false],
    },
    {
      feature: "예정공정표",
      products: [true, true, false, false, false, false, false],
    },
    {
      feature: "안전관리비 계획서",
      products: [true, true, false, false, false, false, false],
    },
    {
      feature: "환경보전비 계획서",
      products: [true, true, false, false, false, false, false],
    },
    {
      feature: "직접시공계획서",
      products: [true, true, false, false, false, false, false],
    },
    {
      feature: "선급금 신청서",
      products: [true, true, false, false, false, false, false],
    },
    {
      feature: "두 줄로 나누기",
      products: [true, true, false, true, false, false, false],
    },
    {
      feature: "증감+실정보고서",
      products: [true, true, false, true, false, false, false],
    },
    {
      feature: "변경도서",
      products: [true, true, false, true, false, false, false],
    },
    {
      feature: "변경 준공만들기",
      products: [true, true, false, true, false, false, false],
    },
    {
      feature: "변경 한줄만들기",
      products: [true, true, false, true, false, false, false],
    },
    {
      feature: "변경 색상교체",
      products: [true, true, false, true, false, false, false],
    },
    {
      feature: "기성 내역서",
      products: [true, true, false, false, false, false, false],
    },
    {
      feature: "기성계 서류",
      products: [true, true, false, false, false, false, false],
    },
    {
      feature: "준공 내역서",
      products: [true, true, false, false, false, false, false],
    },
    {
      feature: "준공계 서류",
      products: [true, true, false, false, false, false, false],
    },
    {
      feature: "안전관리비 사용내역",
      products: [true, true, false, false, false, false, false],
    },
    {
      feature: "환경보전비 사용내역",
      products: [true, true, false, false, false, false, false],
    },
    {
      feature: "청구서류",
      products: [true, true, false, false, false, false, false],
    },
  ];

  // 권한 캐시 저장 헬퍼 함수
  const saveProductPermissionsToCache = async (productId: number) => {
    try {
      const response = await dupLogin.checkDupLogin(() =>
        api.checkSubscriptionOverlapAsync({ product_ids: [productId] })
      );

      if (response?.status === 200 && response?.data) {
        const data = response.data;
        const permissions = new Set<string>();

        if (data.new_permissions) {
          data.new_permissions.forEach((perm: any) => permissions.add(perm.title));
        }
        if (data.overlapping_permissions) {
          data.overlapping_permissions.forEach((perm: any) => permissions.add(perm.title));
        }

        console.log(`💾 상품 ${productId}의 권한 캐시 저장:`, Array.from(permissions));

        setSelectedProductPermissions(prev => {
          const newMap = new Map(prev);
          newMap.set(productId, permissions);
          return newMap;
        });

        return permissions;
      }
    } catch (error) {
      console.log(`권한 캐시 저장 실패 (상품 ${productId})`);
    }
    return null;
  };

  // 할인율 계산 함수
  const getDiscountRange = (forceSubscription?: boolean) => {
    if (products.length === 0) {
      return { min: 0, max: 0, message: "" };
    }

    const discounts: number[] = [];
    const useSubscription = forceSubscription !== undefined ? forceSubscription : isSubscription;

    products.forEach((product) => {
      let discount = 0;

      if (isYearly) {
        // 연간 결제
        if (useSubscription) {
          // 연간 구독: (월간기준가 * 12 - 연간구독가) / (월간기준가 * 12) * 100
          const monthlyBase = product.regular_monthly_price * 12;
          const yearlySubscription = product.subscription_yearly_price;
          if (monthlyBase > 0) {
            discount = Math.round((monthlyBase - yearlySubscription) / monthlyBase * 100);
          }
        } else {
          // 연간 일반: (월간기준가 * 12 - 연간일반가) / (월간기준가 * 12) * 100
          const monthlyBase = product.regular_monthly_price * 12;
          const yearlyRegular = product.regular_yearly_price;
          if (monthlyBase > 0) {
            discount = Math.round((monthlyBase - yearlyRegular) / monthlyBase * 100);
          }
        }
      } else {
        // 월간 결제
        if (useSubscription) {
          // 월간 구독: (월간기준가 - 월간구독가) / 월간기준가 * 100
          if (product.regular_monthly_price > 0) {
            discount = Math.round((product.regular_monthly_price - product.subscription_monthly_price) / product.regular_monthly_price * 100);
          }
        } else {
          // 월간 일반: 할인 없음
          discount = 0;
        }
      }

      if (discount > 0) {
        discounts.push(discount);
      }
    });

    if (discounts.length === 0) {
      return { min: 0, max: 0, message: "" };
    }

    const min = Math.min(...discounts);
    const max = Math.max(...discounts);

    let message = "";
    if (min === max) {
      message = `${min}% 할인`;
    } else {
      message = `${min}%~${max}% 할인`;
    }

    return { min, max, message };
  };

  // 상위 패키지 선택 시 하위 상품 자동 제거
  const removeSubsetProducts = (newProductId: number, newProductPermissions: Set<string>) => {
    const productsToRemove: number[] = [];

    // 이미 선택된 상품들 중에서 새 상품에 완전히 포함되는 것 찾기
    selectedProductPermissions.forEach((existingPerms, existingProductId) => {
      if (existingProductId === newProductId) return;

      // 기존 상품의 권한이 새 상품의 권한에 모두 포함되면 제거 대상
      const isSubset = Array.from(existingPerms).every(perm =>
        newProductPermissions.has(perm)
      );

      if (isSubset && existingPerms.size > 0) {
        productsToRemove.push(existingProductId);
      }
    });

    if (productsToRemove.length > 0) {
      console.log(`🔄 상위 패키지 선택 - 하위 상품 제거:`, productsToRemove);

      // 선택 목록에서 제거
      setSelectedProducts(prev => prev.filter(id => !productsToRemove.includes(id)));

      // 캐시에서도 제거
      setSelectedProductPermissions(prev => {
        const newMap = new Map(prev);
        productsToRemove.forEach(id => newMap.delete(id));
        return newMap;
      });

      // 제거된 상품명 표시
      const removedNames = products
        .filter(p => productsToRemove.includes(p.id))
        .map(p => p.name)
        .join(', ');

      console.log(`✅ 자동 제거된 상품: ${removedNames}`);
    }
  };

  // 상품 선택 핸들러
  const handleProductSelect = async (productId: number) => {
    // 해제하는 경우 바로 처리
    if (selectedProducts.includes(productId)) {
      const newSelectedProducts = selectedProducts.filter((id) => id !== productId);
      setSelectedProducts(newSelectedProducts);

      // 권한 캐시에서도 제거
      setSelectedProductPermissions(prev => {
        const newMap = new Map(prev);
        newMap.delete(productId);
        return newMap;
      });
      return;
    }

    // 추가하는 경우
    const newSelectedProducts = [...selectedProducts, productId];

    // 일반 결제(비구독)인 경우: 중복 체크 없이 바로 선택
    if (!isSubscription) {
      setSelectedProducts(newSelectedProducts);
      return;
    }

    // 구독 결제인 경우: 중복 체크 수행 (단순 버전 - 상품 ID + 구독 기간만 체크)
    try {
      // 중복 체크: 선택하려는 상품이 이미 구독 중인지 확인
      const response = await dupLogin.checkDupLogin(() =>
        api.checkSubscriptionOverlapAsync({
          product_ids: [productId],  // 단일 상품만 체크
          is_yearly: isYearly
        })
      );

      console.log('🔍 중복 체크 - 상품:', productId);
      console.log('🔍 구독 기간:', isYearly ? '연간' : '월간');
      console.log('🔍 API 응답:', response?.data);

      // 200 OK - 중복 없음 (허용)
      if (response?.status === 200 && response?.data?.code === 1 && response?.data?.should_allow) {
        console.log('✅ 중복 체크 통과 - 바로 선택');
        setSelectedProducts(newSelectedProducts);
      }
      // 400 Bad Request 또는 should_allow = false - 중복 있음 (차단)
      else if (
        (response?.status === 400 || response?.data?.code === 0) ||
        (response?.data?.should_allow === false)
      ) {
        console.log('❌ 중복 발견 - 모달 표시');
        setOverlapData(response?.data);
        setPendingProductId(productId);
        setOverlapModalOpen(true);
      }
      // 기타 오류 - 안전하게 허용
      else {
        console.log('⚠️ 예상치 못한 응답 - 선택 허용');
        setSelectedProducts(newSelectedProducts);
      }
    } catch (error: any) {
      // 네트워크 오류 등: 조용히 선택 허용
      setSelectedProducts(newSelectedProducts);
    }
  };

  // Phase 2: 중복 모달 확인 핸들러
  const handleOverlapConfirm = () => {
    if (pendingProductId) {
      console.log("User confirmed, selecting product:", pendingProductId);
      const newSelectedProducts = [...selectedProducts, pendingProductId];
      setSelectedProducts(newSelectedProducts);
    }
    setOverlapModalOpen(false);
    setOverlapData(null);
    setPendingProductId(null);
  };

  // Phase 2: 중복 모달 닫기 핸들러
  const handleOverlapClose = () => {
    console.log("User cancelled overlap modal");
    setOverlapModalOpen(false);
    setOverlapData(null);
    setPendingProductId(null);
  };

  // Phase 3: 업그레이드 자동 전환 핸들러
  const handleOverlapUpgrade = () => {
    if (!pendingProductId) return;

    console.log("User confirmed upgrade, selecting product:", pendingProductId);
    const newSelectedProducts = [...selectedProducts, pendingProductId];
    setSelectedProducts(newSelectedProducts);

    setOverlapModalOpen(false);
    setPendingProductId(null);
    setOverlapData(null);
  };

  // 가격 계산 (새로운 4가지 결제 유형)
  const calculatePrice = (product: SubscriptionProduct) => {
    let price = 0;
    let priceType = '';
    
    if (isSubscription) {
      // 구독 결제 (자동결제)
      if (isYearly) {
        // 연간 구독 결제 (1년치 자동결제)
        price = product.subscription_yearly_price;
        priceType = '구독 연간';
      } else {
        // 월간 구독 결제 (매월 자동결제)
        price = product.subscription_monthly_price;
        priceType = '구독 월간';
      }
    } else {
      // 일반 결제
      if (isYearly) {
        // 1년 일반결제 (약간 할인)
        price = product.regular_yearly_price;
        priceType = '일반 연간';
      } else {
        // 1개월 일반결제 (기준가, 가장 비쌈)
        price = product.regular_monthly_price;
        priceType = '일반 1개월';
      }
    }
    
    return price;
  };

  // 할인 뱃지 정보 계산
  const getDiscountBadgeInfo = (product: SubscriptionProduct) => {
    // 1개월 일반결제는 기준가이므로 뱃지 없음
    if (!isSubscription && !isYearly) {
      return null;
    }

    const basePrice = product.regular_monthly_price; // 기준가 (1개월 일반결제)
    const currentPrice = calculatePrice(product);
    
    if (!basePrice || basePrice === 0) {
      return null;
    }

    let badgeText = '';
    let badgeColor = '';
    let discountRate = 0;

    if (isSubscription) {
      if (isYearly) {
        // 구독 연간 결제
        badgeText = '구독 연간 할인';
        badgeColor = '#7C3AED'; // Purple color
        // 연간 구독 vs 월간 일반결제 * 12 비교
        const yearlyRegularTotal = basePrice * 12;
        discountRate = Math.round(((yearlyRegularTotal - currentPrice) / yearlyRegularTotal) * 100);
      } else {
        // 구독 월간 결제
        badgeText = '구독 월간 할인';
        badgeColor = '#10B981'; // Green color
        discountRate = Math.round(((basePrice - currentPrice) / basePrice) * 100);
      }
    } else {
      if (isYearly) {
        // 연간 일반결제
        badgeText = '연간 결제 할인';
        badgeColor = '#3B82F6'; // Blue color
        // 연간 일반결제 vs 월간 일반결제 * 12 비교
        const yearlyRegularTotal = basePrice * 12;
        discountRate = Math.round(((yearlyRegularTotal - currentPrice) / yearlyRegularTotal) * 100);
      }
    }

    return {
      text: badgeText,
      color: badgeColor,
      discountRate: discountRate > 0 ? discountRate : 0
    };
  };

  // 할인 뱃지 컴포넌트
  const renderDiscountBadge = (product: SubscriptionProduct) => {
    const badgeInfo = getDiscountBadgeInfo(product);
    
    if (!badgeInfo || badgeInfo.discountRate === 0) {
      return null;
    }

    return (
      <div 
        style={{
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: '20px',
          backgroundColor: badgeInfo.color,
          color: '#FFFFFF',
          fontSize: '12px',
          fontWeight: '600',
          marginBottom: '8px'
        }}
      >
        {badgeInfo.text} {badgeInfo.discountRate}%
      </div>
    );
  };

  // 총 금액 계산 (새로운 4가지 결제 유형)
  const getTotalPrice = () => {
    return selectedProducts.reduce((total, productId) => {
      const product = products.find((p) => p.id === productId);
      if (product) {
        const price = calculatePrice(product);
        return total + price;
      }
      return total;
    }, 0);
  };

  // 일반 결제 처리 (KG이니시스)
  const startRegularPayment = async () => {
    try {
      // 선택된 첫 번째 상품 정보
      const firstProduct = products.find((p) =>
        selectedProducts.includes(p.id)
      );
      if (!firstProduct) {
        alertContext.showAlert("선택된 상품 정보를 찾을 수 없습니다.");
        return;
      }

      // 총 결제 금액 계산
      const totalPrice = getTotalPrice();

      // 무통장 입금의 경우 입금 기한 설정 (3일 후)
      let vbankDue = undefined;
      if (payMethod === "vbank") {
        const date = new Date();
        date.setDate(date.getDate() + 3);
        vbankDue = date.format("yyyyMMdd");
      }

      // 전화번호에서 특수문자 제거 (토스페이먼츠 요구사항)
      const sanitizedPhone = (userInfo?.phone || "").replace(/[^0-9]/g, "");

      // 결제 요청 데이터
      const requestData: IamportPaymentRequestModel = {
        pg: (payMethod === "vbank" || payMethod === "trans") ? GlobalEnvironments.getTransferPgProvider() : GlobalEnvironments.getPgProvider(),
        amount: totalPrice,
        buyer_tel: sanitizedPhone,
        pay_method: payMethod, // 선택된 결제 수단 사용
        merchant_uid: `mid_${new Date().getTime()}`,
        name: firstProduct.name,
        buyer_name: userInfo?.last_name || "",
        buyer_addr: userInfo?.addr || "",
        buyer_email: userInfo?.email || "",
        buyer_postcode: userInfo?.post_code || "",
        custom_data: {
          product_ids: selectedProducts,
          actual_amount: totalPrice,
          is_subscription: false,
          is_yearly: isYearly,
        },
        vbank_due: vbankDue,
        // notice_url을 지정하지 않으면 포트원 대시보드 설정이 자동으로 사용됨
        // 모바일 인앱 브라우저 등에서 JS 콜백이 호출되지 않는 경우를 대비해
        // PaymentComplete 호출에 필요한 정보를 m_redirect_url 쿼리스트링에 모두 포함시킨다.
        m_redirect_url: `${GlobalEnvironments.getAppUrl()}payment?${new URLSearchParams(
          {
            product_ids: selectedProducts.join(", "),
            names: String(firstProduct.name),
            buyer_tel: sanitizedPhone,
            buyer_name: userInfo?.last_name || "",
            buyer_email: userInfo?.email || "",
            pay_method: payMethod,
            actual_amount: String(totalPrice),
            is_yearly: String(isYearly),
          }
        ).toString()}`,
      };

      // KG이니시스 결제창 호출
      logCheckoutStep("pg_open", { pay_method: payMethod || null });
      Imp.requestPay(requestData, handleRegularPaymentCallback);
    } catch (error) {
      console.error("일반 결제 오류:", error);
      alertContext.showAlert("결제 처리 중 오류가 발생했습니다.");
    }
  };

  // 일반 결제 콜백 처리 (KG이니시스)
  const handleRegularPaymentCallback = async (
    res: ImpCallbackResponseModel
  ) => {
    try {
      // 토스페이먼츠는 success/imp_success를 반환하지 않으므로
      // error_code, error_msg, imp_uid로 결제 실패/취소 판단
      const hasError = res?.error_code || res?.error_msg;
      const noImpUid = !res?.imp_uid;
      logCheckoutStep("pg_result", {
        result: hasError || noImpUid ? (res?.error_msg?.includes("취소") ? "cancel" : "error") : "ok",
        pay_method: payMethod || res?.pay_method || null,
        error_code: res?.error_code ?? null,
        error_msg: String(res?.error_msg || "").slice(0, 300),
      });

      if (hasError || noImpUid) {
        if (res?.error_msg?.includes("취소")) {
          alertContext.showAlert("사용자의 요청으로 취소되었습니다.");
          return;
        }
        if (!res?.error_msg) {
          alertContext.showAlert(
            "결제가 정상적으로 완료되지 않았습니다.\n우측하단 고객센터를 통해 문의 바랍니다."
          );
        } else {
          alertContext.showAlert(res.error_msg);
        }
        return;
      }

      // custom_data가 콜백 응답에 없을 수 있으므로 원본 데이터 추가
      // 무통장 입금의 경우 res.paid_amount가 없을 수 있으므로 getTotalPrice()로 계산
      const paymentData = {
        ...res,
        pay_method: res.pay_method || payMethod, // 토스페이먼츠 응답에 pay_method가 없을 수 있음
        custom_data: res.custom_data || {
          product_ids: selectedProducts,
          actual_amount: res.paid_amount || getTotalPrice(),
          is_subscription: false,
          is_yearly: isYearly,
        },
      };

      // 결제 완료 API 호출
      const completeRes = await dupLogin.checkDupLogin(() =>
        api.postPaymentCompleteAsync(paymentData)
      );

      if (completeRes?.status === 200) {
        // 카드 결제인 경우
        if ((payMethod || res.pay_method) === IamportPayMethod.card) {
          alertContext.showAlert("결제가 완료되었습니다.", () => {
            history.push(pages.paymentComplete);
          });
        } else {
          // 무통장 입금 또는 실시간 계좌이체인 경우 - 가상계좌 정보 표시
          const vbankData = completeRes?.data;
          const vbankDate = vbankData?.vbank_date
            ? new Date(vbankData.vbank_date * 1000).toLocaleDateString("ko-KR", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "";
          alertContext.showAlert(
            `가상계좌가 발급되었습니다.\n\n` +
            `은행: ${vbankData?.vbank_name || ""}\n` +
            `계좌번호: ${vbankData?.vbank_num || ""}\n` +
            `입금기한: ${vbankDate}\n\n` +
            `위 계좌로 입금해주시면 결제가 완료됩니다.\n\n` +
            `※ 가상계좌 정보는 마이페이지 결제이력의\n'계좌 확인'을 통해서도 확인 가능합니다.`,
            () => {
              history.push(pages.mypage + "/payment");
            },
            false, // isEnterClose
            true   // disableBackdropClick - 확인 버튼만 눌러야 닫힘
          );
        }
      } else if ([400, 500].includes(completeRes?.status)) {
        alertContext.showAlert(
          "결제 중 오류가 발생하였습니다.\n" + (completeRes?.message ?? "")
        );
      }
    } catch (error) {
      console.error("일반 결제 처리 오류:", error);
      alertContext.showAlert(
        "결제 처리 중 오류가 발생하였습니다.\n고객센터를 통해 문의 부탁드립니다."
      );
    }
  };

  // 계측 2차: 결제 버튼 이후 단계(카드 입력 이탈). 카드 값은 절대 싣지 않는다.
  const logCheckoutStep = (step: string, meta: Record<string, any> = {}) => {
    logEvent("checkout_step", step, {
      product_ids: selectedProducts,
      is_yearly: isYearly,
      is_subscription: isSubscription,
      ...meta,
    });
  };

  // 결제하기 버튼 클릭 핸들러
  const handlePaymentClick = async () => {
    // 계측: 결제 버튼 클릭 (상품 선택·약관·로그인 검증 전 시점)
    logEvent("payment_click", isSubscription ? "subscription" : "onetime", {
      product_ids: selectedProducts,
      is_yearly: isYearly,
      is_subscription: isSubscription,
      has_saved_card: !!savedCardInfo,
      agree_all: agreeAll,
    });
    if (selectedProducts.length === 0) {
      logCheckoutStep("blocked", { reason: "no_product" });
      alertContext.showAlert("결제할 상품을 선택해주세요.");
      return;
    }

    // 약관 동의 체크
    if (!agreeAll) {
      logCheckoutStep("blocked", { reason: "terms" });
      alertContext.showAlert("이용약관 및 개인정보 처리방침에 동의해주세요.");
      return;
    }

    // 로그인 체크
    const key = util.getCookie(Cookies.authKey);
    if (!key) {
      alertContext.showAlert("로그인 후 이용해주세요.");
      history.push(pages.login);
      return;
    } else {
      const res = await dupLogin.checkDupLogin(() => api.getMyInfo());
      if (!res || res?.status === 401) {
        logCheckoutStep("blocked", { reason: "session" });
        return;
      }
    }

    if (isSubscription) {
      // 구독형 결제: 저장된 카드 정보가 있으면 바로 결제, 없으면 카드 정보 입력
      if (savedCardInfo) {
        // 저장된 카드 정보가 있으면 확인 후 바로 정기결제 실행 (새 빌링키 발급)
        const cardLast4 = savedCardInfo.cardNumber.split('-').pop() || '';
        const confirmMessage = `저장된 카드 정보로 구독 결제를 진행하시겠습니까?\n\n카드 끝 네자리: ${cardLast4}`;

        const accepted = window.confirm(confirmMessage);
        logCheckoutStep("saved_card_confirm", { accepted });
        if (accepted) {
          await startSubscriptionPaymentWithSavedCard();
        }
      } else {
        // 저장된 카드 정보가 없으면 카드 정보 입력 모달 표시
        logCheckoutStep("card_modal_open");
        setShowCardModal(true);
      }
    } else {
      // 일반 결제 처리 - KG이니시스 결제 모달
      await startRegularPayment();
    }
  };

  // 카드 정보 입력 완료 후 처리 (새 카드 정보로 빌키 발급 + 첫 결제)
  const handleCardInfoSubmit = async (cardInfo: BillingCardInfo) => {
    // 카드 등록 API 결과(성공·실패 메시지)는 서버가 checkout_step/card_register 로 남긴다
    logCheckoutStep("card_submit", {
      card_type: cardInfo.cardType === "사업자카드" ? "business" : "personal",
    });
    try {
      setIsPaymentLoading(true);
      setShowCardModal(false);

      console.log("카드 정보 제출:", {
        cardNumber: cardInfo.cardNumber,
        cardType: cardInfo.cardType,
        expiryDate: cardInfo.expiryDate,
        birthOrBusiness: cardInfo.birthOrBusiness,
        password2Digits: cardInfo.password2Digits,
      });

      // API 요청 데이터 구성
      const expiryParts = cardInfo.expiryDate.split("/"); // "04/27" → ["04", "27"]
      const requestData: any = {
        card_number: cardInfo.cardNumber.replace(/[^0-9]/g, ""),
        expiry_month: expiryParts[0] || "",
        expiry_year: expiryParts[1] || "",
        pwd_2digit: cardInfo.password2Digits || "",
      };

      // 카드 타입에 따라 birth 또는 business_num 분리
      if (cardInfo.cardType === "개인카드") {
        requestData.birth = cardInfo.birthOrBusiness;
      } else {
        requestData.business_num = cardInfo.birthOrBusiness;
      }

      console.log("API 요청 데이터:", requestData);

      // 새 카드 정보로 빌키 발급 및 첫 결제 진행
      const billingRes = await dupLogin.checkDupLogin(() =>
        api.postBillingKeyOnlyAsync(requestData)
      );

      console.log("빌링 API 응답:", {
        status: billingRes?.status,
        data: billingRes?.data,
        message: billingRes?.message,
      });

      if (billingRes?.status === 200 && billingRes.data?.code === 1) {
        // 카드 정보 저장 성공
        console.log("카드 등록 성공, 첫 결제 진행");
        setSavedCardInfo(cardInfo);

        // 첫 결제 진행 (카드 정보 전달)
        await startSubscriptionPayment(cardInfo);
      } else {
        // 기술적 정보는 콘솔에만 로깅
        console.error("카드 등록 실패:", {
          status: billingRes?.status,
          code: billingRes?.data?.code,
          message: billingRes?.data?.message || billingRes?.message,
        });

        // 고객에게는 백엔드 메시지만 표시
        const errorMessage = billingRes?.data?.message || billingRes?.message;
        alertContext.showAlert(
          errorMessage || "카드 등록에 실패했습니다. 카드 정보를 확인 후 다시 시도해주세요."
        );
      }
    } catch (error) {
      console.error("카드 정보 등록 오류:", error);
      alertContext.showAlert(
        "카드 등록 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요."
      );
    } finally {
      setIsPaymentLoading(false);
    }
  };

  // 정기결제 실행 (저장된 카드 정보로 새 빌키 발급 + 첫 결제)
  const handleSubscriptionPayment = async () => {
    setShowPaymentModal(false);
    await startSubscriptionPayment();
  };

  // 모달 닫기 핸들러들
  const handleCardModalClose = () => {
    setShowCardModal(false);
  };

  const handlePaymentModalClose = () => {
    setShowPaymentModal(false);
  };

  // 빌링 정보 로드 함수
  const loadBillingInfo = async () => {
    try {
      const cardInfoRes = await dupLogin.checkDupLogin(() =>
        api.getSubscriptionBillingAsync()
      );
      if (cardInfoRes?.status === 200 && cardInfoRes.data?.code === 1) {
        const billingInfo = cardInfoRes.data.billing_info;
        if (billingInfo) {
          setSavedCardInfo({
            cardNumber: `****-****-****-${billingInfo.card_last4}`,
            cardType:
              billingInfo.card_brand || billingInfo.card_name || "등록된 카드",
            expiryDate: "",
            holderName: userInfo?.last_name || "카드 소유자",
            birthOrBusiness: "",
            password2Digits: "",
          });

          // 구독 상태 정보 설정 (API 응답에서 구독 관련 정보 확인)
          console.log("빌링 정보 전체:", billingInfo); // 디버깅용
          
          // 더 엄격한 구독 상태 확인 - 실제 활성 구독이 있는지 확인
          const hasActiveSubscription = billingInfo.subscription_id && 
                                       billingInfo.subscription_id !== null &&
                                       billingInfo.subscription_id !== '' &&
                                       (billingInfo.subscription_status === 'active' ||
                                        billingInfo.status === 'active' ||
                                        billingInfo.is_subscribed === true);
          
          setSubscriptionStatus({
            isActive: hasActiveSubscription || false,
            subscriptionId: billingInfo.subscription_id,
            nextPaymentDate: billingInfo.next_payment_date
          });
        } else {
          setSavedCardInfo(null);
          setSubscriptionStatus({
            isActive: false,
            subscriptionId: undefined,
            nextPaymentDate: undefined
          });
        }
      }
    } catch (error) {
      console.log("빌링 정보 로드 실패:", error);
      setSavedCardInfo(null);
      setSubscriptionStatus({
        isActive: false,
        subscriptionId: undefined,
        nextPaymentDate: undefined
      });
    }
  };

  // 구독 관리 페이지로 이동 (MyPage로 리디렉션)
  const handleGoToSubscriptionManagement = () => {
    history.push('/mypage');
  };

  // 저장된 카드 정보로 바로 구독 결제 실행 (새 빌링키 발급)
  const startSubscriptionPaymentWithSavedCard = async () => {
    try {
      setIsPaymentLoading(true);

      // 구독 결제 시작 API 호출 (저장된 카드 정보로 새 빌링키 발급 + 첫 결제 + 스케줄 등록)
      const totalPrice = getTotalPrice();
      const subscriptionRes = await dupLogin.checkDupLogin(
        () => api.postExistingBillingPaymentAsync({
          is_yearly: isYearly,
          product_ids: selectedProducts,
          actual_amount: totalPrice
        }) // 저장된 카드로 새 빌링키 발급 및 결제
      );

      if (subscriptionRes?.status === 200 && subscriptionRes.data?.success) {
        alertContext.showAlert(
          "구독이 시작되었습니다!",
          () => {
            history.push(pages.paymentComplete);
          }
        );

        // 빌링 정보 새로고침
        await loadBillingInfo();
      } else {
        const errorMessage = subscriptionRes?.data?.message || subscriptionRes?.message;
        logCheckoutStep("subscription_start_fail", {
          saved_card: true,
          http_status: subscriptionRes?.status ?? null,
          message: String(errorMessage || "").slice(0, 300),
        });
        alertContext.showAlert(
          errorMessage || "구독 시작에 실패했습니다. 잠시 후 다시 시도해주세요."
        );
      }
    } catch (error) {
      console.error("구독 결제 시작 오류:", error);
      alertContext.showAlert("구독 시작 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setIsPaymentLoading(false);
    }
  };

  // 구독 결제 처리 (V2 API - 모달 없이 직접 결제 처리)
  const startSubscriptionPayment = async (cardInfo?: BillingCardInfo) => {
    const productIds = selectedProducts;
    if (productIds.length === 0) {
      alertContext.showAlert("상품을 선택해주세요.");
      return;
    }

    try {
      setIsPaymentLoading(true);

      console.log("V2 구독 결제 시작:", {
        selectedProducts: productIds,
        isYearly: isYearly,
        userEmail: userInfo?.email,
        hasCardInfo: !!cardInfo,
      });

      // 카드 정보 구성
      const totalPrice = getTotalPrice();
      let requestData: any = {
        is_yearly: isYearly,
        product_ids: productIds,
        actual_amount: totalPrice
      };

      // 카드 정보가 있으면 포함
      if (cardInfo) {
        const expiryStr = cardInfo.expiry || "";
        requestData.card_info = {
          card_number: (cardInfo.card_number || "").replace(/[^0-9]/g, ""),
          expiry_month: expiryStr.length >= 7 ? expiryStr.substring(5, 7) : "",
          expiry_year: expiryStr.length >= 4 ? expiryStr.substring(2, 4) : "",
          birth_or_business: cardInfo.birth || cardInfo.business_num || "",
          pwd_2digit: cardInfo.pwd_2digit || "",
        };
      }

      console.log("V2 구독 결제 요청 데이터:", requestData);

      // V2 API를 통한 직접 구독 결제 (카드 정보로 빌링키 발급 + 첫 결제 + 정기결제 등록)
      const subscriptionRes = await dupLogin.checkDupLogin(
        () => api.postExistingBillingPaymentAsync(requestData)
      );

      console.log("V2 구독 결제 응답:", {
        status: subscriptionRes?.status,
        data: subscriptionRes?.data,
        success: subscriptionRes?.data?.success,
      });

      if (subscriptionRes?.status === 200 && subscriptionRes.data?.success) {
        alertContext.showAlert(
          "구독이 시작되었습니다!",
          () => {
            history.push(pages.paymentComplete);
          }
        );

        // 빌링 정보 새로고침
        await loadBillingInfo();
      } else {
        console.error("V2 구독 결제 실패:", subscriptionRes);
        const errorMessage = subscriptionRes?.data?.message || subscriptionRes?.message;
        logCheckoutStep("subscription_start_fail", {
          saved_card: !cardInfo,
          http_status: subscriptionRes?.status ?? null,
          message: String(errorMessage || "").slice(0, 300),
        });
        alertContext.showAlert(
          errorMessage || "구독 결제에 실패했습니다. 잠시 후 다시 시도해주세요."
        );
      }
    } catch (error) {
      console.error("V2 구독 결제 오류:", error);
      alertContext.showAlert(
        "구독 결제 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요."
      );
    } finally {
      setIsPaymentLoading(false);
    }
  };

  // V2 API 사용으로 handleSubscriptionCallback 제거됨
  // (직접 API 호출 방식으로 콜백 불필요)

  useEffect(() => {
    const handleScroll = () => {
      if (tableRef.current && headerRef.current) {
        const tableRect = tableRef.current.getBoundingClientRect();
        const tableTop = tableRect.top;
        const tableBottom = tableRect.bottom;
        const headerHeight = headerRef.current.offsetHeight;

        // 메인 헤더 높이 계산
        const mainHeader = document.querySelector(
          "header.sticky-top"
        ) as HTMLElement;
        const mainHeaderHeight = mainHeader ? mainHeader.offsetHeight : 0;

        // 테이블이 메인 헤더 아래로 지나가고, 테이블 하단이 아직 보일 때 헤더 고정
        const shouldFix =
          tableTop <= mainHeaderHeight &&
          tableBottom > headerHeight + mainHeaderHeight + 50;

        // 테이블 위치와 크기 업데이트
        setTablePosition({
          left: tableRect.left,
          width: tableRect.width,
        });

        // 가로 스크롤 위치 업데이트
        setScrollLeft(tableRef.current.scrollLeft);

        // 실제 셀 너비 측정 (첫 번째 데이터 행의 셀들)
        const firstDataRow = tableRef.current.querySelector(
          "tbody tr:first-child"
        );
        if (firstDataRow) {
          const cells = firstDataRow.querySelectorAll("td");
          const widths = Array.from(cells).map(
            (cell) => cell.getBoundingClientRect().width
          );
          setCellWidths(widths);
        }

        if (shouldFix !== isHeaderFixed) {
          setIsHeaderFixed(shouldFix);
        }
      }
    };

    const handleTableScroll = () => {
      if (tableRef.current) {
        setScrollLeft(tableRef.current.scrollLeft);
      }
    };

    // 스크롤 성능 최적화를 위한 throttle
    let ticking = false;
    const throttledScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", throttledScrollHandler);
    window.addEventListener("resize", throttledScrollHandler);

    // 테이블 컨테이너의 가로 스크롤 이벤트 추가
    if (tableRef.current) {
      tableRef.current.addEventListener("scroll", handleTableScroll);
    }

    return () => {
      window.removeEventListener("scroll", throttledScrollHandler);
      window.removeEventListener("resize", throttledScrollHandler);
      if (tableRef.current) {
        tableRef.current.removeEventListener("scroll", handleTableScroll);
      }
    };
  }, [isHeaderFixed]);

  // 초기 테이블 위치 계산
  useEffect(() => {
    const calculateInitialPosition = () => {
      if (tableRef.current) {
        const tableRect = tableRef.current.getBoundingClientRect();
        setTablePosition({
          left: tableRect.left,
          width: tableRect.width,
        });

        // 초기 셀 너비 측정
        const firstDataRow = tableRef.current.querySelector(
          "tbody tr:first-child"
        );
        if (firstDataRow) {
          const cells = firstDataRow.querySelectorAll("td");
          const widths = Array.from(cells).map(
            (cell) => cell.getBoundingClientRect().width
          );
          setCellWidths(widths);
        }
      }
    };

    // 컴포넌트 마운트 후 위치 계산
    calculateInitialPosition();

    // 약간의 지연 후 다시 계산 (레이아웃이 완전히 로드된 후)
    const timer = setTimeout(calculateInitialPosition, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>이용권 요금제 | 콘엑스 - 건설 적산 프로그램</title>
        <meta name="description" content="콘엑스 이용권 요금제 안내. 월간/연간 구독으로 AI 기반 건설 적산, 견적서 자동화, 일위대가 검증 서비스를 이용하세요." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <main id="wrap">
        {/* 애니메이션 스타일 추가 */}
        <style>{animationStyles}</style>
        <article>
        {/* STEP 01 서비스 선택 */}
        <section>
          <div className="container content">
            <h3
              className="marketing-title pb-3 aos-init aos-animate mb-7"
              data-aos="fade-up"
              data-aos-anchor-placement="top-bottom"
            >
              고객님의 비즈니스에 <span>적합한 서비스</span>를 선택하세요.
            </h3>

            {/* 결제 옵션 */}
            <div className="d-flex flex-column align-items-center gap-4 mb-6">
              {/* 월간/연간 결제 모던 토글 */}
              <div
                className="modern-toggle-container"
                style={{
                  background: "#f8f9fa",
                  borderRadius: "50px",
                  padding: "6px",
                  display: "flex",
                  position: "relative",
                  boxShadow: "inset 0 2px 6px rgba(0,0,0,0.1)",
                  border: "1px solid #e9ecef",
                }}
              >
                {/* 슬라이딩 배경 */}
                <div
                  style={{
                    position: "absolute",
                    top: "6px",
                    left: isYearly ? "50%" : "6px",
                    width: "calc(50% - 6px)",
                    height: "calc(100% - 12px)",
                    background:
                      "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                    borderRadius: "44px",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: "0 4px 12px rgba(0, 123, 255, 0.3)",
                    zIndex: 1,
                  }}
                />

                <button
                  type="button"
                  onClick={() => {
                    setIsYearly(false);
                    // 월간/연간 전환 시 선택한 상품 초기화
                    setSelectedProducts([]);
                    setSelectedProductPermissions(new Map());
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    borderRadius: "44px",
                    padding: "12px 24px",
                    fontWeight: "600",
                    fontSize: "15px",
                    color: !isYearly ? "#ffffff" : "#6c757d",
                    transition: "all 0.3s ease",
                    position: "relative",
                    zIndex: 2,
                    minWidth: "100px",
                  }}
                >
                  월간
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setIsYearly(true);
                    // 월간/연간 전환 시 선택한 상품 초기화
                    setSelectedProducts([]);
                    setSelectedProductPermissions(new Map());
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    borderRadius: "44px",
                    padding: "12px 24px",
                    fontWeight: "600",
                    fontSize: "15px",
                    color: isYearly ? "#ffffff" : "#6c757d",
                    transition: "all 0.3s ease",
                    position: "relative",
                    zIndex: 2,
                    minWidth: "100px",
                  }}
                >
                  연간
                  <span
                    className="ms-2"
                    style={{
                      background: isYearly ? "rgba(255, 255, 255, 0.2)" : "#28a745",
                      color: "#ffffff",
                      fontSize: "11px",
                      padding: "4px 8px",
                      borderRadius: "12px",
                      fontWeight: "500",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: "1",
                    }}
                  >
                    추천
                  </span>
                </button>
              </div>

              {/* 구독형 결제 모던 체크박스 */}
              <div
                className="modern-checkbox-container"
                style={{
                  background: isSubscription
                    ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)"
                    : "#ffffff",
                  borderRadius: "16px",
                  padding: "18px 24px",
                  border: isSubscription
                    ? "2px solid #28a745"
                    : "2px solid #e9ecef",
                  boxShadow: isSubscription
                    ? "0 6px 24px rgba(40, 167, 69, 0.25), 0 0 0 3px rgba(40, 167, 69, 0.1)"
                    : "0 4px 16px rgba(0,0,0,0.08)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  transform: isSubscription
                    ? "translateY(-1px)"
                    : "translateY(0)",
                  position: "relative",
                  overflow: "hidden",
                }}
                onClick={() => setIsSubscription(!isSubscription)}
              >
                {/* 반짝이는 효과 */}
                {isSubscription && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-1px",
                      left: "-1px",
                      right: "-1px",
                      bottom: "-1px",
                      background:
                        "linear-gradient(45deg, #28a745, #20c997, #28a745)",
                      borderRadius: "17px",
                      opacity: 0.4,
                      zIndex: -1,
                      filter: "blur(6px)",
                    }}
                  />
                )}

                <div
                  className="d-flex align-items-center"
                  style={{ gap: "24px" }}
                >
                  <div
                    className="d-flex align-items-center"
                    style={{ flex: 1 }}
                  >
                    {/* 커스텀 체크박스 */}
                    <div
                      style={{
                        width: "22px",
                        height: "22px",
                        borderRadius: "6px",
                        border: isSubscription
                          ? "2px solid #ffffff"
                          : "2px solid #dee2e6",
                        background: isSubscription ? "#ffffff" : "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.3s ease",
                        marginRight: "12px",
                        boxShadow: isSubscription
                          ? "0 3px 8px rgba(40, 167, 69, 0.25)"
                          : "0 2px 6px rgba(0,0,0,0.08)",
                      }}
                    >
                      {isSubscription && (
                        <span
                          style={{
                            color: "#28a745",
                            fontSize: "14px",
                            fontWeight: "bold",
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </div>

                    <div>
                      <span
                        className="fw-semibold"
                        style={{
                          fontSize: "16px",
                          color: isSubscription ? "#ffffff" : "#495057",
                          transition: "color 0.3s ease",
                          display: "block",
                        }}
                      >
                        구독형 결제로 추가 할인받기
                      </span>
                    </div>
                  </div>

                  {/* 할인 배지 */}
                  <div
                    style={{
                      background: isSubscription
                        ? "rgba(255,255,255,0.2)"
                        : "#ff6b35",
                      color: isSubscription ? "#ffffff" : "#ffffff",
                      fontSize: "12px",
                      fontWeight: "600",
                      padding: "6px 12px",
                      borderRadius: "20px",
                      border: isSubscription
                        ? "1px solid rgba(255,255,255,0.3)"
                        : "none",
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isSubscription ? "할인 적용됨" : getDiscountRange(true).message || "할인 혜택"}
                  </div>
                </div>

                {/* 숨겨진 실제 체크박스 */}
                <input
                  type="checkbox"
                  checked={isSubscription}
                  onChange={() => {}}
                  style={{ display: "none" }}
                />
              </div>
            </div>

            {/* 구독 결제 안내 (구독형 결제 선택 시에만 표시) */}
            {isSubscription && (
              <div
                className="subscription-info-container"
                style={{
                  background: "#f8f9fa",
                  borderRadius: "16px",
                  padding: "20px",
                  border: "1px solid #e9ecef",
                  marginBottom: "24px",
                }}
              >
                <h6 className="fw-semibold mb-3 d-flex align-items-center">
                  <span className="material-symbols-outlined me-2 text-warning">
                    info
                  </span>
                  구독 결제 안내
                </h6>
                <ul className="mb-0" style={{ paddingLeft: "20px" }}>
                  <li className="mb-2">
                    {isYearly ? '매년' : '매월'} 결제일에 <strong>등록하신 결제수단</strong>으로 자동
                    결제됩니다.
                  </li>
                  <li>
                    언제든지 <strong>마이페이지</strong>에서 구독을 중단할 수
                    있습니다.
                  </li>
                </ul>
              </div>
            )}

            {/* 첫 구독 프로모션 배너 (구독 결제 + 자격 있는 사용자에게만 표시) - 프로모션 기능 비활성화 */}
            {false && isSubscription && promotionEligibility?.isEligible && (
              <div
                className="promotion-banner-container"
                style={{
                  background: "linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)",
                  borderRadius: "16px",
                  padding: "24px",
                  marginBottom: "24px",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  boxShadow: "0 8px 32px rgba(255, 107, 53, 0.25)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* 배경 장식 */}
                <div
                  style={{
                    position: "absolute",
                    top: "-50%",
                    right: "-10%",
                    width: "200px",
                    height: "200px",
                    background: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "50%",
                    filter: "blur(40px)",
                    zIndex: 1,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: "-30%",
                    left: "-10%",
                    width: "150px",
                    height: "150px",
                    background: "rgba(255, 255, 255, 0.08)",
                    borderRadius: "50%",
                    filter: "blur(30px)",
                    zIndex: 1,
                  }}
                />

                <div style={{ position: "relative", zIndex: 2 }}>
                  {/* 헤더 */}
                  <div className="d-flex align-items-center mb-3">
                    <div
                      style={{
                        background: "rgba(255, 255, 255, 0.2)",
                        borderRadius: "12px",
                        padding: "8px 16px",
                        marginRight: "12px",
                      }}
                    >
                      <span
                        style={{
                          color: "#ffffff",
                          fontSize: "14px",
                          fontWeight: "700",
                          letterSpacing: "0.5px",
                        }}
                      >
                        🎉 첫 구독 특별 혜택
                      </span>
                    </div>
                    <div
                      style={{
                        background: "rgba(255, 255, 255, 0.95)",
                        color: "#ff6b35",
                        fontSize: "12px",
                        fontWeight: "700",
                        padding: "4px 10px",
                        borderRadius: "20px",
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                      }}
                    >
                      LIMITED TIME
                    </div>
                  </div>

                  {/* 메인 혜택 */}
                  <h5
                    className="fw-bold mb-3"
                    style={{
                      color: "#ffffff",
                      fontSize: "20px",
                      lineHeight: "1.3",
                    }}
                  >
                    {promotionEligibility.description}
                    <span
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "400",
                        marginTop: "4px",
                        color: "rgba(255, 255, 255, 0.9)",
                      }}
                    >
                      구독 시작 후 {promotionEligibility.benefitDays}일간 무료로 이용하세요!
                    </span>
                  </h5>

                  {/* 혜택 리스트 */}
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center mb-2">
                        <span
                          className="me-2"
                          style={{
                            color: "#ffffff",
                            fontSize: "16px",
                          }}
                        >
                          ✓
                        </span>
                        <span
                          style={{
                            color: "#ffffff",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          무료 체험 기간 중 모든 기능 이용 가능
                        </span>
                      </div>
                      <div className="d-flex align-items-center">
                        <span
                          className="me-2"
                          style={{
                            color: "#ffffff",
                            fontSize: "16px",
                          }}
                        >
                          ✓
                        </span>
                        <span
                          style={{
                            color: "#ffffff",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          언제든지 무료로 취소 가능
                        </span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center mb-2">
                        <span
                          className="me-2"
                          style={{
                            color: "#ffffff",
                            fontSize: "16px",
                          }}
                        >
                          ✓
                        </span>
                        <span
                          style={{
                            color: "#ffffff",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          무료 기간 종료 전 알림 제공
                        </span>
                      </div>
                      <div className="d-flex align-items-center">
                        <span
                          className="me-2"
                          style={{
                            color: "#ffffff",
                            fontSize: "16px",
                          }}
                        >
                          ✓
                        </span>
                        <span
                          style={{
                            color: "#ffffff",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          무료 기간 후 자동 구독 전환
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 주의사항 */}
                  <div
                    style={{
                      background: "rgba(255, 255, 255, 0.15)",
                      borderRadius: "12px",
                      padding: "16px",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <span
                        className="material-symbols-outlined me-2"
                        style={{
                          color: "#ffffff",
                          fontSize: "20px",
                          marginTop: "2px",
                        }}
                      >
                        info
                      </span>
                      <div>
                        <p
                          className="mb-2"
                          style={{
                            color: "#ffffff",
                            fontSize: "13px",
                            fontWeight: "600",
                            lineHeight: "1.4",
                          }}
                        >
                          <strong>무료 체험 종료 안내</strong>
                        </p>
                        <p
                          className="mb-0"
                          style={{
                            color: "rgba(255, 255, 255, 0.9)",
                            fontSize: "12px",
                            lineHeight: "1.5",
                          }}
                        >
                          • 무료 체험 기간이 종료되면 자동으로 유료 구독으로 전환됩니다.
                          <br />
                          • 체험 종료 3일 전에 이메일과 SMS로 미리 알려드립니다.
                          <br />
                          • 무료 체험 중에도 마이페이지에서 언제든 구독을 중단할 수 있습니다.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 상품 목록 */}
            <div
              className="tab-content pt-6"
              id="subscriptionContent"
              style={{ userSelect: "auto" }}
            >
              <div
                className="tab-pane fade show active"
                role="tabpanel"
                tabIndex={0}
                style={{ userSelect: "auto" }}
              >
                {isProductsLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">
                      상품 정보를 불러오는 중...
                    </p>
                  </div>
                ) : products.length === 0 ? (
                  <div className="text-center py-5">
                    <h5 className="text-muted">
                      상품 정보를 불러올 수 없습니다.
                    </h5>
                    <button
                      className="btn btn-primary mt-3"
                      onClick={() => window.location.reload()}
                    >
                      다시 시도
                    </button>
                  </div>
                ) : (
                  <>
                    {/* 저장된 결제수단 표시 */}
                    {savedCardInfo && isSubscription && (
                      <div 
                        className="saved-payment-method mb-5"
                        style={{
                          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                          borderRadius: "16px",
                          padding: "24px",
                          border: "1px solid #dee2e6",
                          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)"
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <div
                              style={{
                                width: "48px",
                                height: "48px",
                                background: "linear-gradient(135deg, #007AFF 0%, #0056b3 100%)",
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginRight: "16px"
                              }}
                            >
                              <span 
                                className="material-symbols-outlined" 
                                style={{ color: "#ffffff", fontSize: "24px" }}
                              >
                                credit_card
                              </span>
                            </div>
                            <div>
                              <h6 
                                className="mb-1 fw-semibold" 
                                style={{ color: "#1D1D1F", fontSize: "16px" }}
                              >
                                등록된 결제수단
                              </h6>
                              <div className="d-flex align-items-center gap-2">
                                <span
                                  className="fw-semibold"
                                  style={{ color: "#495057", fontSize: "14px" }}
                                >
                                  {savedCardInfo.cardNumber}
                                </span>
                              </div>
                              <small className="text-muted">
                                {subscriptionStatus?.isActive 
                                  ? "이 카드로 구독 결제가 진행 중입니다"
                                  : "구독 결제 시 이 카드로 자동 결제됩니다"
                                }
                              </small>
                              
                              {/* 구독 상태 표시 */}
                              {subscriptionStatus?.isActive && (
                                <div className="mt-2">
                                  <span 
                                    style={{
                                      background: "linear-gradient(135deg, #20c997 0%, #17a2b8 100%)",
                                      color: "#ffffff",
                                      fontSize: "11px",
                                      padding: "4px 10px",
                                      borderRadius: "12px",
                                      fontWeight: "600",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px"
                                    }}
                                  >
                                    <span 
                                      className="material-symbols-outlined" 
                                      style={{ fontSize: "12px" }}
                                    >
                                      autorenew
                                    </span>
                                    구독 중
                                  </span>
                                  {subscriptionStatus.nextPaymentDate && (
                                    <span 
                                      className="ms-2"
                                      style={{
                                        fontSize: "10px",
                                        color: "#6c757d",
                                        fontWeight: "500"
                                      }}
                                    >
                                      다음 결제: {new Date(subscriptionStatus.nextPaymentDate).toLocaleDateString('ko-KR')}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-end">
                            <div
                              style={{
                                background: subscriptionStatus?.isActive 
                                  ? "rgba(32, 201, 151, 0.1)" 
                                  : "rgba(40, 167, 69, 0.1)",
                                color: subscriptionStatus?.isActive ? "#20c997" : "#28a745",
                                padding: "6px 12px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: "600",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px"
                              }}
                            >
                              <span 
                                className="material-symbols-outlined" 
                                style={{ fontSize: "14px" }}
                              >
                                {subscriptionStatus?.isActive ? "autorenew" : "check_circle"}
                              </span>
                              {subscriptionStatus?.isActive ? "구독 활성" : "등록됨"}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="payment-wrap">
                    {/* 첫 번째 줄 - 3개 */}
                    <div className="row g-4 mb-4">
                      {products.slice(0, 3).map((product) => (
                        <div key={product.id} className="col-lg-4 col-md-4">
                          <div
                            onClick={() => handleProductSelect(product.id)}
                            style={{
                              cursor: "pointer",
                              background: "#ffffff",
                              border: selectedProducts.includes(product.id)
                                ? "2px solid #007AFF"
                                : "1px solid #D1D1D6",
                              borderRadius: "16px",
                              boxShadow: "0 2px 16px rgba(0, 0, 0, 0.08)",
                              transition: "all 0.2s ease",
                              position: "relative",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            {product.isPopular && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "16px",
                                  right: "16px",
                                  background: "#ff6b35",
                                  color: "white",
                                  padding: "4px 12px",
                                  borderRadius: "12px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  zIndex: 10,
                                }}
                              >
                                인기
                              </div>
                            )}

                            <div
                              style={{
                                padding: "32px 24px",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              {/* 상단 부제목 */}
                              <p
                                style={{
                                  color: "#8E8E93",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  margin: "0 0 8px 0",
                                  lineHeight: "1.4",
                                  userSelect: "none",
                                }}
                              >
                                {product.description}
                              </p>

                              {/* 메인 타이틀 */}
                              <h3
                                style={{
                                  fontSize: "28px",
                                  fontWeight: "700",
                                  color: "#1D1D1F",
                                  margin: "0 0 32px 0",
                                  lineHeight: "1.2",
                                  whiteSpace: "pre-line",
                                  userSelect: "none",
                                }}
                              >
                                {product.name}
                              </h3>

                              {/* 기능 리스트 */}
                              <div style={{ flex: "1", marginBottom: "32px", userSelect: "none" }}>
                                {product.features.map((feature, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      marginBottom: "16px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "20px",
                                        height: "20px",
                                        borderRadius: "50%",
                                        background: "#34C759",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginRight: "12px",
                                        marginTop: "2px",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <span
                                        style={{
                                          color: "white",
                                          fontSize: "12px",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        ✓
                                      </span>
                                    </div>
                                    <span
                                      style={{
                                        color: "#1D1D1F",
                                        fontSize: "16px",
                                        fontWeight: "400",
                                        lineHeight: "1.5",
                                      }}
                                    >
                                      {feature}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              {/* 가격 영역 - 새로운 표시 형식 */}
                              <div style={{ marginBottom: "24px" }}>
                                {/* 할인 뱃지 */}
                                {renderDiscountBadge(product)}
                                
                                {/* 실제 결제 금액 */}
                                <div
                                  style={{
                                    fontSize: "36px",
                                    fontWeight: "700",
                                    color: isSubscription ? "#34C759" : "#1D1D1F",
                                    lineHeight: "1.1",
                                    marginBottom: "8px",
                                  }}
                                >
                                  {isYearly ? (
                                    // 연간 결제시 월 환산 금액을 크게 표시 (총 결제금액 / 12)
                                    <>
                                      월 {util.addComma(
                                        Math.floor(calculatePrice(product) / 12)
                                      )}
                                      원
                                    </>
                                  ) : (
                                    // 월간 결제시: 구독이면 '월' 표시, 아니면 표시 안함
                                    <>
                                      {isSubscription && "월 "}{util.addComma(calculatePrice(product))}원
                                    </>
                                  )}
                                </div>

                                {/* 총 결제금액 표시 */}
                                <div
                                  style={{
                                    color: "#8E8E93",
                                    fontSize: "16px",
                                    fontWeight: "500",
                                  }}
                                >
                                  {isYearly ? (
                                    // 연간 결제시 총 결제금액 작게 표시
                                    <div>
                                      총 결제금액{" "}
                                      {util.addComma(calculatePrice(product))}
                                      원
                                    </div>
                                  ) : (
                                    // 월간 결제시는 월 결제 표시
                                    <div>월 결제</div>
                                  )}
                                </div>
                              </div>

                              {/* 선택 버튼 */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleProductSelect(product.id);
                                }}
                                style={{
                                  background: selectedProducts.includes(
                                    product.id
                                  )
                                    ? "#007AFF"
                                    : "#F2F2F7",
                                  color: selectedProducts.includes(product.id)
                                    ? "white"
                                    : "#007AFF",
                                  border: "none",
                                  borderRadius: "12px",
                                  padding: "16px 24px",
                                  fontSize: "17px",
                                  fontWeight: "600",
                                  width: "100%",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                {selectedProducts.includes(product.id)
                                  ? "선택됨"
                                  : "선택하기"}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* 두 번째 줄 - 4개 */}
                    <div className="row g-4">
                      {products.slice(3, 7).map((product) => (
                        <div
                          key={product.id}
                          className="col-lg-3 col-md-3 col-sm-6"
                        >
                          <div
                            onClick={() => handleProductSelect(product.id)}
                            style={{
                              cursor: "pointer",
                              background: "#ffffff",
                              border: selectedProducts.includes(product.id)
                                ? "2px solid #007AFF"
                                : "1px solid #D1D1D6",
                              borderRadius: "16px",
                              boxShadow: "0 2px 16px rgba(0, 0, 0, 0.08)",
                              transition: "all 0.2s ease",
                              position: "relative",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            {product.isPopular && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "16px",
                                  right: "16px",
                                  background: "#ff6b35",
                                  color: "white",
                                  padding: "4px 12px",
                                  borderRadius: "12px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  zIndex: 10,
                                }}
                              >
                                인기
                              </div>
                            )}

                            <div
                              style={{
                                padding: "32px 24px",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              {/* 상단 부제목 */}
                              <p
                                style={{
                                  color: "#8E8E93",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  margin: "0 0 8px 0",
                                  lineHeight: "1.4",
                                  userSelect: "none",
                                }}
                              >
                                {product.description}
                              </p>

                              {/* 메인 타이틀 */}
                              <h3
                                style={{
                                  fontSize: "28px",
                                  fontWeight: "700",
                                  color: "#1D1D1F",
                                  margin: "0 0 32px 0",
                                  lineHeight: "1.2",
                                  whiteSpace: "pre-line",
                                  userSelect: "none",
                                }}
                              >
                                {product.name}
                              </h3>

                              {/* 기능 리스트 */}
                              <div style={{ flex: "1", marginBottom: "32px", userSelect: "none" }}>
                                {product.features.map((feature, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      marginBottom: "16px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "20px",
                                        height: "20px",
                                        borderRadius: "50%",
                                        background: "#34C759",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginRight: "12px",
                                        marginTop: "2px",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <span
                                        style={{
                                          color: "white",
                                          fontSize: "12px",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        ✓
                                      </span>
                                    </div>
                                    <span
                                      style={{
                                        color: "#1D1D1F",
                                        fontSize: "16px",
                                        fontWeight: "400",
                                        lineHeight: "1.5",
                                      }}
                                    >
                                      {feature}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              {/* 가격 영역 - 새로운 표시 형식 */}
                              <div style={{ marginBottom: "24px" }}>
                                {/* 할인 뱃지 */}
                                {renderDiscountBadge(product)}
                                
                                {/* 실제 결제 금액 */}
                                <div
                                  style={{
                                    fontSize: "36px",
                                    fontWeight: "700",
                                    color: isSubscription ? "#34C759" : "#1D1D1F",
                                    lineHeight: "1.1",
                                    marginBottom: "8px",
                                  }}
                                >
                                  {isYearly ? (
                                    // 연간 결제시 월 환산 금액을 크게 표시 (총 결제금액 / 12)
                                    <>
                                      월 {util.addComma(
                                        Math.floor(calculatePrice(product) / 12)
                                      )}
                                      원
                                    </>
                                  ) : (
                                    // 월간 결제시: 구독이면 '월' 표시, 아니면 표시 안함
                                    <>
                                      {isSubscription && "월 "}{util.addComma(calculatePrice(product))}원
                                    </>
                                  )}
                                </div>

                                {/* 총 결제금액 표시 */}
                                <div
                                  style={{
                                    color: "#8E8E93",
                                    fontSize: "16px",
                                    fontWeight: "500",
                                  }}
                                >
                                  {isYearly ? (
                                    // 연간 결제시 총 결제금액 작게 표시
                                    <div>
                                      총 결제금액{" "}
                                      {util.addComma(calculatePrice(product))}
                                      원
                                    </div>
                                  ) : (
                                    // 월간 결제시는 월 결제 표시
                                    <div>월 결제</div>
                                  )}
                                </div>
                              </div>

                              {/* 선택 버튼 */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleProductSelect(product.id);
                                }}
                                style={{
                                  background: selectedProducts.includes(
                                    product.id
                                  )
                                    ? "#007AFF"
                                    : "#F2F2F7",
                                  color: selectedProducts.includes(product.id)
                                    ? "white"
                                    : "#007AFF",
                                  border: "none",
                                  borderRadius: "12px",
                                  padding: "16px 24px",
                                  fontSize: "17px",
                                  fontWeight: "600",
                                  width: "100%",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                {selectedProducts.includes(product.id)
                                  ? "선택됨"
                                  : "선택하기"}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* 결제 하단 바 */}
        {selectedProducts.length > 0 && (
          <div
            className="payment-bottom"
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              borderTop: "1px solid #e0e0e0",
              padding: "16px 0",
              boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
              zIndex: 1000,
              transform: "translateY(0)",
              transition: "transform 0.3s ease-in-out",
            }}
          >
            <div className="container">
              <div
                className="payment-bottom-wrap"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: "16px",
                }}
              >
                <div
                  className="pay-price"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    color: "#333333",
                  }}
                >
                  <div>
                    <span
                      className="material-symbols-outlined"
                      style={{ color: "#007AFF", fontSize: "24px" }}
                    >
                      shopping_bag
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="price"
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#333333",
                        display: "flex",
                        alignItems: "baseline",
                      }}
                    >
                      <span>{util.addComma(getTotalPrice())}</span>원
                      {isYearly && (
                        <span
                          style={{
                            color: "#666666",
                            fontSize: "14px",
                            marginLeft: "8px",
                            lineHeight: "1",
                          }}
                        >
                          / 연간
                        </span>
                      )}
                      {!isYearly && (
                        <span
                          style={{
                            color: "#666666",
                            fontSize: "14px",
                            marginLeft: "8px",
                            lineHeight: "1",
                          }}
                        >
                          / 월간
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* 기간제 이용권 결제 수단 선택 */}
                {!isSubscription && (
                  <div
                    className="payment-method-selector"
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <button
                      type="button"
                      className={`btn ${payMethod === "card" ? "btn-primary" : "btn-outline-primary"}`}
                      style={{
                        fontSize: "14px",
                        padding: "8px 16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                      onClick={() => setPayMethod("card")}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>
                        credit_card
                      </span>
                      신용카드
                    </button>
                    <button
                      type="button"
                      className={`btn ${payMethod === "vbank" ? "btn-primary" : "btn-outline-primary"}`}
                      style={{
                        fontSize: "14px",
                        padding: "8px 16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                      onClick={() => setPayMethod("vbank")}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>
                        savings
                      </span>
                      무통장 입금
                    </button>
                    <button
                      type="button"
                      className={`btn ${payMethod === "trans" ? "btn-primary" : "btn-outline-primary"}`}
                      style={{
                        fontSize: "14px",
                        padding: "8px 16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                      onClick={() => setPayMethod("trans")}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>
                        account_balance
                      </span>
                      실시간 계좌이체
                    </button>
                  </div>
                )}

                {/* 약관 동의 섹션 (간소화) */}
                <div className="agreements-section mt-3">
                  <div className="form-check d-flex align-items-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="agreeAllSubscription"
                      checked={agreeAll}
                      onChange={(e) => handleAgreeAll(e.target.checked)}
                      style={{
                        width: "18px",
                        height: "18px",
                        marginRight: "10px",
                        borderColor: "#0d6efd",
                        borderWidth: "2px"
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="agreeAllSubscription"
                      style={{ fontSize: "14px", lineHeight: "1.5" }}
                    >
                      <span
                        className="text-primary"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowTermsModal(true)}
                      >
                        이용약관
                      </span>
                      <span> 및 </span>
                      <span
                        className="text-primary"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowPrivacyModal(true)}
                      >
                        개인정보 처리방침
                      </span>
                      <span>에 동의합니다 (필수)</span>
                    </label>
                  </div>
                  <div className="text-muted mt-2" style={{ marginLeft: "28px", fontSize: "13px" }}>
                    ※ 환불규정:{" "}
                    <span
                      style={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => setShowRefundModal(true)}
                    >
                      상세보기
                    </span>
                  </div>
                </div>

                {/* 자동 갱신 안내 (구독 결제인 경우만) */}
                {isSubscription && (
                  <div
                    className="auto-renewal-notice mt-2 p-2"
                    style={{
                      background: "#fff3cd",
                      borderRadius: "6px",
                      border: "1px solid #ffc107",
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <span
                        className="material-symbols-outlined me-2 text-warning"
                        style={{ fontSize: "16px" }}
                      >
                        info
                      </span>
                      <div className="small">
                        <strong>자동 갱신 안내</strong>
                        <br />
                        본 구독은 결제 주기에 따라 자동으로 갱신되며,
                        <br />
                        해지하지 않으면 동일 금액이 자동 결제됩니다.
                        <br />
                        해지는 마이페이지 또는 고객센터(ddk@ddukeong.com)를 통해 가능합니다.
                      </div>
                    </div>
                  </div>
                )}

                <div className="payment-bottom-btn mt-3">
                  <button
                    type="button"
                    className="btn btn-primary btn-lg left-icon"
                    onClick={handlePaymentClick}
                    disabled={!agreeAll}
                  >
                    <span className="material-symbols-outlined me-2">paid</span>
                    {isSubscription ? "구독결제하기" : "결제하기"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 구독 카드 등록 모달 */}
        <SubscriptionCardModal
          show={showCardModal}
          onHide={handleCardModalClose}
          onConfirm={handleCardInfoSubmit}
          userInfo={userInfo}
          totalPrice={getTotalPrice()}
          isLoading={isPaymentLoading}
        />

        {/* Phase 2 & 3: 중복 구독 체크 모달 */}
        <SubscriptionOverlapModal
          open={overlapModalOpen}
          onClose={handleOverlapClose}
          onConfirm={handleOverlapConfirm}
          onUpgrade={handleOverlapUpgrade}
          overlapData={overlapData}
        />

        {/* 정기결제 실행 모달 */}
        {showPaymentModal && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex={-1}
            role="dialog"
            onClick={handlePaymentModalClose}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              role="document"
              style={{ maxWidth: "500px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="modal-content"
                style={{
                  borderRadius: "20px",
                  border: "none",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                }}
              >
                <div
                  className="modal-header"
                  style={{
                    borderBottom: "1px solid #e9ecef",
                    padding: "24px 32px 20px",
                    borderRadius: "20px 20px 0 0",
                  }}
                >
                  <div className="w-100">
                    <h4
                      className="modal-title fw-bold mb-2"
                      style={{ color: "#1D1D1F", fontSize: "24px" }}
                    >
                      정기결제 실행
                    </h4>
                    <p
                      className="text-muted mb-0"
                      style={{ fontSize: "14px", lineHeight: "1.4" }}
                    >
                      등록된 카드로 정기결제를 실행합니다
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handlePaymentModalClose}
                    disabled={isPaymentLoading}
                    style={{
                      opacity: 0.7,
                      fontSize: "20px",
                      marginLeft: "16px",
                    }}
                  />
                </div>

                <div className="modal-body" style={{ padding: "24px 32px" }}>
                  {/* 등록된 카드 정보 */}
                  {savedCardInfo && (
                    <div
                      className="saved-card-info mb-4"
                      style={{
                        background: "#f8f9fa",
                        borderRadius: "12px",
                        padding: "20px",
                        border: "1px solid #e9ecef",
                      }}
                    >
                      <h6 className="fw-semibold mb-3 d-flex align-items-center">
                        <span className="material-symbols-outlined me-2 text-primary">
                          credit_card
                        </span>
                        사용할 카드
                      </h6>
                      <div className="d-flex align-items-center">
                        <div
                          className="card-icon me-3"
                          style={{
                            width: "48px",
                            height: "32px",
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <span
                            className="material-symbols-outlined text-white"
                            style={{ fontSize: "18px" }}
                          >
                            credit_card
                          </span>
                        </div>
                        <div>
                          <div className="fw-semibold text-dark mb-1">
                            {savedCardInfo.cardNumber}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 결제 정보 */}
                  <div
                    className="payment-info"
                    style={{
                      background: "#ffffff",
                      border: "2px solid #007AFF",
                      borderRadius: "12px",
                      padding: "20px",
                    }}
                  >
                    <h6 className="fw-semibold mb-3 d-flex align-items-center">
                      <span className="material-symbols-outlined me-2 text-primary">
                        receipt_long
                      </span>
                      결제 정보
                    </h6>
                    <div className="row">
                      <div className="col-6">
                        <small className="text-muted">결제 금액</small>
                        <div
                          className="fw-bold"
                          style={{ fontSize: "18px", color: "#007AFF" }}
                        >
                          100원
                        </div>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">선택한 상품 수</small>
                        <div className="fw-bold" style={{ fontSize: "18px" }}>
                          {selectedProducts.length}개
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 주의사항 */}
                  <div
                    className="alert alert-info mt-4"
                    style={{
                      background: "rgba(13, 202, 240, 0.1)",
                      border: "1px solid rgba(13, 202, 240, 0.3)",
                      borderRadius: "10px",
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <span
                        className="material-symbols-outlined me-2 text-info"
                        style={{ fontSize: "20px", marginTop: "2px" }}
                      >
                        info
                      </span>
                      <div>
                        <small className="text-info fw-semibold">
                          결제 안내
                        </small>
                        <div
                          className="text-info"
                          style={{
                            fontSize: "13px",
                            lineHeight: "1.4",
                            marginTop: "4px",
                          }}
                        >
                          • 실제 결제가 진행됩니다 (100원)
                          <br />
                          • 등록된 결제 수단으로 자동 결제됩니다
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="modal-footer"
                  style={{
                    borderTop: "1px solid #e9ecef",
                    padding: "20px 32px 24px",
                    borderRadius: "0 0 20px 20px",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-outline-secondary me-3"
                    onClick={handlePaymentModalClose}
                    disabled={isPaymentLoading}
                    style={{
                      borderRadius: "10px",
                      padding: "12px 24px",
                      fontWeight: "600",
                    }}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubscriptionPayment}
                    disabled={isPaymentLoading}
                    style={{
                      borderRadius: "10px",
                      padding: "12px 24px",
                      fontWeight: "600",
                      minWidth: "140px",
                    }}
                  >
                    {isPaymentLoading ? (
                      <>
                        <div
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          style={{ width: "16px", height: "16px" }}
                        />
                        결제 중...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined me-2">
                          paid
                        </span>
                        100원 결제하기
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </article>

      {/* 이용약관 모달 */}
      {showTermsModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.7)",
            zIndex: 1060,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowTermsModal(false);
          }}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
            <div className="modal-content" style={{ borderRadius: "12px" }}>
              <div className="modal-header">
                <h5 className="modal-title fw-bold">이용약관</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowTermsModal(false)}
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: "60vh" }}>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "keep-all",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  {termsOfService}
                </pre>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowTermsModal(false)}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 개인정보 처리방침 모달 */}
      {showPrivacyModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.7)",
            zIndex: 1060,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowPrivacyModal(false);
          }}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
            <div className="modal-content" style={{ borderRadius: "12px" }}>
              <div className="modal-header">
                <h5 className="modal-title fw-bold">개인정보 처리방침</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPrivacyModal(false)}
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: "60vh" }}>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "keep-all",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  {privacyPolicy}
                </pre>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowPrivacyModal(false)}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 환불규정 모달 */}
      {showRefundModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.7)",
            zIndex: 1060,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowRefundModal(false);
          }}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
            <div className="modal-content" style={{ borderRadius: "12px" }}>
              <div className="modal-header">
                <h5 className="modal-title fw-bold">환불 및 청약철회 규정</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRefundModal(false)}
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: "60vh" }}>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "keep-all",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  {refundPolicy}
                </pre>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowRefundModal(false)}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
    </>
  );
};

export default SubscriptionPaymentContainer;
